/**
 * Graceful Creations — Personalisation Studio
 *
 * Drives the live preview and mirrors the chosen font stack / colour hex
 * into the two hidden backend properties.
 *
 * Implemented as a custom element so each block instance wires itself up
 * on connect. That matters in the theme editor, which tears down and
 * re-renders blocks on every settings change — a one-shot DOMContentLoaded
 * listener would go dead after the first edit.
 */

const PLACEHOLDER_FALLBACK = 'Their name';

/** Relative luminance per WCAG, so we can pick a readable text shadow. */
function luminance(hex) {
  const clean = String(hex || '').replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;

  if (!/^[0-9a-f]{6}$/i.test(full)) return 1;

  const channel = (pair) => {
    const v = parseInt(pair, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };

  const r = channel(full.slice(0, 2));
  const g = channel(full.slice(2, 4));
  const b = channel(full.slice(4, 6));

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

class GcPersonalisation extends HTMLElement {
  connectedCallback() {
    this.nameInput = this.querySelector('[data-gc-name]');
    this.preview = this.querySelector('[data-gc-preview]');
    this.counter = this.querySelector('[data-gc-counter]');
    this.fontStackInput = this.querySelector('[data-gc-font-stack]');
    this.colourHexInput = this.querySelector('[data-gc-colour-hex]');

    // The block renders nothing when the product isn't personalisable.
    if (!this.nameInput) return;

    this.rows = this.querySelector('[data-gc-rows]');
    this.props = this.querySelector('[data-gc-props]');
    this.addBtn = this.querySelector('[data-gc-add]');
    this.tally = this.querySelector('[data-gc-tally]');
    this.formId = this.nameInput.getAttribute('form');
    this.maxNames = parseInt(this.addBtn?.dataset.max, 10) || 1;
    // The property key is a block setting, so read it rather than assume 'Name'.
    this.nameKey = this.rows?.dataset.gcNameKey || 'Name';

    this.onRowsInput = this.syncNames.bind(this);
    this.onRowsClick = this.handleRowClick.bind(this);
    this.rows?.addEventListener('input', this.onRowsInput);
    this.rows?.addEventListener('click', this.onRowsClick);

    if (this.addBtn) {
      this.onAdd = this.addRow.bind(this);
      this.addBtn.addEventListener('click', this.onAdd);
    }

    // The rows are the quantity, so the theme's own selector would be a
    // second source of truth. Hiding it is done here rather than in CSS
    // because it should only disappear where this block actually runs.
    this.quantityInput = this.productForm()?.querySelector('input[name="quantity"]');
    this.quantityInput?.closest('.quantity-selector-wrapper')?.setAttribute('hidden', '');

    this.syncNames();

    this.placeholder = this.preview
      ? this.preview.textContent.trim() || PLACEHOLDER_FALLBACK
      : PLACEHOLDER_FALLBACK;
    this.maxChars = parseInt(this.nameInput.getAttribute('maxlength'), 10) || 22;

    this.onNameInput = this.paintName.bind(this);
    this.onFontChange = this.paintFont.bind(this);
    this.onColourChange = this.paintColour.bind(this);

    this.nameInput.addEventListener('input', this.onNameInput);
    this.fontInputs().forEach((el) => el.addEventListener('change', this.onFontChange));
    this.colourInputs().forEach((el) => el.addEventListener('change', this.onColourChange));

    this.paintName();
    this.paintFont();
    this.paintColour();
  }

  disconnectedCallback() {
    this.nameInput?.removeEventListener('input', this.onNameInput);
    this.rows?.removeEventListener('input', this.onRowsInput);
    this.rows?.removeEventListener('click', this.onRowsClick);
    this.addBtn?.removeEventListener('click', this.onAdd);
    this.fontInputs().forEach((el) => el.removeEventListener('change', this.onFontChange));
    this.colourInputs().forEach((el) => el.removeEventListener('change', this.onColourChange));
  }

  productForm() {
    return this.formId ? document.getElementById(this.formId) : null;
  }

  rowEls() {
    return Array.from(this.rows?.querySelectorAll('[data-gc-row]') || []);
  }

  addRow() {
    const existing = this.rowEls();
    if (existing.length >= this.maxNames) return;

    const clone = existing[0].cloneNode(true);
    const name = clone.querySelector('[data-gc-name]');
    const qty = clone.querySelector('[data-gc-qty]');

    // The first row owns the id the label points at, and the hooks the
    // preview and counter read. A clone must not carry either.
    name.removeAttribute('id');
    name.removeAttribute('data-gc-name');
    name.value = '';
    qty.value = '1';

    this.rows.appendChild(clone);
    this.syncNames();
    name.focus();
  }

  handleRowClick(event) {
    const del = event.target.closest('[data-gc-del]');
    if (!del) return;

    const row = del.closest('[data-gc-row]');
    // Never remove the first row: it holds the label's target and the
    // required attribute that blocks an empty add to cart.
    if (row && this.rowEls().indexOf(row) > 0) {
      row.remove();
      this.syncNames();
    }
  }

  /**
   * Read every row and write the real line item properties.
   *
   * One name at quantity one submits plain `Name`, byte-identical to what
   * this block sent before rows existed. Anything more switches to numbered
   * keys so each is a separate line on the invoice and in the email.
   */
  syncNames() {
    const rows = this.rowEls();
    const entries = rows
      .map((row) => ({
        name: (row.querySelector('.gc-nrow__name')?.value || '').trim(),
        qty: Math.max(1, parseInt(row.querySelector('[data-gc-qty]')?.value, 10) || 1),
      }))
      .filter((e) => e.name);

    const total = entries.reduce((sum, e) => sum + e.qty, 0);

    if (this.props) {
      this.props.textContent = '';
      const single = entries.length === 1 && entries[0].qty === 1;

      entries.forEach((entry, i) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        if (this.formId) input.setAttribute('form', this.formId);
        input.name = single
          ? `properties[${this.nameKey}]`
          : `properties[${this.nameKey} ${i + 1}]`;
        input.value = entry.qty > 1 ? `${entry.name} × ${entry.qty}` : entry.name;
        this.props.appendChild(input);
      });
    }

    if (this.quantityInput && total > 0) {
      this.quantityInput.value = String(total);
      // Tinker's quantity component listens for change, not assignment.
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // The first row cannot be removed — it holds the label's target and the
    // required attribute. Its button is made invisible rather than hidden,
    // so the row keeps its third column and the quantity field stays in line
    // with the QTY header above it.
    rows.forEach((row, i) => {
      const del = row.querySelector('[data-gc-del]');
      if (!del) return;
      const first = i === 0;
      del.classList.toggle('is-hidden', first);
      del.tabIndex = first ? -1 : 0;
      del.setAttribute('aria-hidden', first ? 'true' : 'false');
    });

    if (this.addBtn) this.addBtn.hidden = rows.length >= this.maxNames;

    if (this.tally) {
      const show = entries.length > 1 || (entries[0]?.qty || 1) > 1;
      this.tally.hidden = !show;
      if (show) {
        this.tally.textContent =
          `${total} ${total === 1 ? 'piece' : 'pieces'} across ` +
          `${entries.length} ${entries.length === 1 ? 'name' : 'names'}.`;
      }
    }
  }

  fontInputs() {
    return Array.from(this.querySelectorAll('input[name="properties[Font]"]'));
  }

  colourInputs() {
    return Array.from(this.querySelectorAll('input[name="properties[Colour]"]'));
  }

  paintName() {
    const raw = this.nameInput.value || '';

    if (this.preview) {
      this.preview.textContent = raw.trim() || this.placeholder;
    }

    if (this.counter) {
      this.counter.textContent = `${raw.length} / ${this.maxChars}`;
      this.counter.classList.toggle('is-full', raw.length >= this.maxChars);
    }
  }

  paintFont() {
    const selected = this.fontInputs().find((el) => el.checked);
    if (!selected) return;

    const stack = selected.getAttribute('data-stack') || '';
    if (this.preview) this.preview.style.fontFamily = stack;
    if (this.fontStackInput) this.fontStackInput.value = stack;
  }

  paintColour() {
    const selected = this.colourInputs().find((el) => el.checked);
    if (!selected) return;

    const hex = selected.getAttribute('data-hex') || '#ffffff';
    if (this.colourHexInput) this.colourHexInput.value = hex;
    if (!this.preview) return;

    this.preview.style.color = hex;

    // Product photos vary, so shadow the name against its own lightness
    // rather than assuming a dark background.
    this.preview.style.textShadow =
      luminance(hex) > 0.45
        ? '0 3px 14px rgba(0,0,0,.65), 0 0 2px rgba(0,0,0,.55)'
        : '0 2px 10px rgba(255,255,255,.7), 0 0 2px rgba(255,255,255,.9)';
  }
}

if (!customElements.get('gc-personalisation-component')) {
  customElements.define('gc-personalisation-component', GcPersonalisation);
}
